/**
 * Structured ICE diagnostics for the PWA peer lifecycle.
 *
 * Goal: when a P2P link fails we must be able to answer "where did it break?"
 * from a single log line - which attempt/phase we were in, whether gathering
 * ever finished, which candidate types were produced, whether a pair was
 * actually selected, which route carried it, and the final error.
 */

/** Dial phase the attempt belongs to (mirrors iceConfig's phases). */
export type IceDialPhase = 'stun' | 'turn';

/** ICE gathering state reported by the peer connection. */
export type IceGatheringState = 'new' | 'gathering' | 'complete';

/** Connection/ICE state of the peer connection at the time of the record. */
export type IceConnectionState =
  | 'new'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'failed'
  | 'closed';

/** Transport that carried (or would have carried) the link. */
export type IceRoute = 'lan' | 'stun' | 'turn' | 'unknown';

/** Counts of the candidate types the browser produced for one attempt. */
export interface IceCandidateTypeCounts {
  host: number;
  srflx: number;
  prflx: number;
  relay: number;
}

export interface IcePairDetail {
  localType: string;
  remoteType: string;
  localAddress: string;
  remoteAddress: string;
  /** True when the browser marked the pair as nominated/succeeded. */
  selected: boolean;
}

/** Live ICE status snapshot of a peer connection. */
export interface IceStatus {
  gatheringState: IceGatheringState;
  connectionState: IceConnectionState;
  candidates: IceCandidateTypeCounts;
}

export interface IceAttemptRecord {
  /** 1-based attempt counter across the whole dial sequence. */
  attempt: number;
  phase: IceDialPhase;
  /** ICE servers actually handed to RTCPeerConnection for this attempt. */
  servers: string[];
  gatheringState: IceGatheringState;
  connectionState: IceConnectionState;
  candidates: IceCandidateTypeCounts;
  pair: IcePairDetail | null;
  route: IceRoute;
  /** Final error text for the attempt (null while it is still running). */
  error: string | null;
  /** Wall-clock duration of the attempt in ms (null while still running). */
  durationMs: number | null;
}

/** Creates a zeroed candidate counter. */
export function emptyCandidateCounts(): IceCandidateTypeCounts {
  return { host: 0, srflx: 0, prflx: 0, relay: 0 };
}

/** Classifies an ICE candidate line ("candidate:... typ host") into its type. */
export function candidateTypeOf(line: string): keyof IceCandidateTypeCounts | null {
  const m = / typ (host|srflx|prflx|relay)\b/.exec(line);
  if (!m) return null;
  return m[1] as keyof IceCandidateTypeCounts;
}

/** Counts one candidate line into the counter map. */
export function countCandidate(
  counts: IceCandidateTypeCounts,
  line: string
): IceCandidateTypeCounts {
  const type = candidateTypeOf(line);
  if (!type) return counts;
  counts[type] += 1;
  return counts;
}

const CANDIDATE_LOG_LABEL: Record<keyof IceCandidateTypeCounts, string> = {
  host: 'host',
  srflx: 'sf',
  prflx: 'prflx',
  relay: 't',
};

/** Formats candidate counters for logs (abbreviated protocol terms). */
export function formatCandidateCounts(counts: IceCandidateTypeCounts): string {
  const parts: string[] = [];
  for (const key of ['host', 'srflx', 'prflx', 'relay'] as const) {
    if (counts[key] > 0) parts.push(`${CANDIDATE_LOG_LABEL[key]}:${counts[key]}`);
  }
  return parts.join(' ');
}

/**
 * Renders one attempt as a single diagnostic line. Kept stable so the desktop
 * host log and the PWA console can be grepped the same way.
 */
function pairTypeForLog(typ: string): string {
  if (typ === 'srflx') return 'sf';
  if (typ === 'relay') return 't';
  return typ;
}

export function formatIceAttempt(record: IceAttemptRecord): string {
  const phaseLabel = record.phase === 'turn' ? 't' : record.phase === 'stun' ? 's' : record.phase;
  const parts: string[] = [
    `connectivity attempt#${record.attempt}`,
    `phase=${phaseLabel}`,
    `gathering=${record.gatheringState}`,
    `conn=${record.connectionState}`,
  ];
  const cand = formatCandidateCounts(record.candidates);
  if (cand) parts.push(`candidates[${cand}]`);
  if (record.pair) {
    parts.push(
      `pair=${pairTypeForLog(record.pair.localType)}<->${pairTypeForLog(record.pair.remoteType)}` +
        ` (${record.pair.localAddress || '?'} / ${record.pair.remoteAddress || '?'})` +
        `${record.pair.selected ? ' selected' : ''}`
    );
  } else {
    parts.push('pair=none');
  }
  parts.push(`route=${record.route === 'turn' ? 't' : record.route}`);
  if (record.durationMs !== null) parts.push(`${record.durationMs}ms`);
  if (record.error) parts.push(`error="${record.error}"`);
  return parts.join(' ');
}

/**
 * Collects per-attempt ICE diagnostics and keeps the last attempt available
 * for the UI (e.g. an error banner) without re-parsing log lines.
 */
export class IceDiagnostics {
  private attempt = 0;
  private last: IceAttemptRecord | null = null;

  /** Starts a new attempt record and returns it for mutation. */
  begin(phase: IceDialPhase, servers: RTCIceServer[]): IceAttemptRecord {
    this.attempt += 1;
    const record: IceAttemptRecord = {
      attempt: this.attempt,
      phase,
      servers: servers.flatMap((s) =>
        Array.isArray(s.urls) ? [...s.urls] : s.urls ? [s.urls] : []
      ),
      gatheringState: 'new',
      connectionState: 'new',
      candidates: emptyCandidateCounts(),
      pair: null,
      route: 'unknown',
      error: null,
      durationMs: null,
    };
    this.last = record;
    return record;
  }

  /** The most recent attempt record (null before the first attempt). */
  get current(): IceAttemptRecord | null {
    return this.last;
  }

  /** Logs the current attempt in a single greppable line. */
  log(record: IceAttemptRecord | null = this.last): void {
    if (!record) return;
    console.info(formatIceAttempt(record));
  }

  /** Marks the current attempt as finished and logs it. */
  finish(record: IceAttemptRecord, error?: string | null): IceAttemptRecord {
    record.error = error ?? null;
    record.durationMs = record.durationMs ?? 0;
    this.log(record);
    return record;
  }
}
